<template>
  <div
    class="call-container"
  >
    <div
      v-if="callState === 'calling'"
      class="calling-overlay"
    >
      <div class="calling-content">
        <template v-if="ui.rejected">
          <div class="calling-animation">
            <svg
              class="phone-icon"
              style="color: #ef4444"
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="currentColor"
            >
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
            </svg>
          </div>
          <h2 class="calling-title">
            Chamada Recusada
          </h2>
          <p class="calling-subtitle">
            O paciente recusou a chamada. Fechando...
          </p>
        </template>
        <template v-else>
          <div class="calling-animation">
            <div class="pulse-ring" />
            <div class="pulse-ring delay-1" />
            <div class="pulse-ring delay-2" />
            <svg
              class="phone-icon"
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="currentColor"
            >
              <path
                d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
              />
            </svg>
          </div>
          <h2 class="calling-title">
            Chamando...
          </h2>
          <p class="calling-subtitle">
            Aguardando paciente atender
          </p>
          <button
            class="cancel-call-btn"
            @click="cancelCall"
          >
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="currentColor"
            >
              <path
                d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.948.948 0 0 1-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"
              />
            </svg>
            Cancelar Chamada
          </button>
        </template>
      </div>
    </div>

    <template v-else>
      <CallTopBar
        :connected="state.connected"
        :started-at="startedAt"
        :current-time="now"
        :professional-name="callData?.professionalName"
      />

      <main class="video-grid">
        <VideoTile
          variant="remote"
          :stream="ui.swapped ? state.localStream : state.remoteStream"
          :name="
            ui.swapped
              ? callData?.professionalName
              : callData?.patientName || 'Paciente'
          "
          :role="ui.swapped ? 'Profissional' : 'Paciente'"
          :label="
            ui.swapped
              ? callData?.professionalName
              : callData?.patientName || 'Paciente'
          "
          :show-self="ui.swapped"
          :audio-muted="ui.swapped ? false : state.remoteAudioMuted"
          @dblclick="swapVideos"
        />

        <VideoTile
          variant="local"
          :stream="ui.swapped ? state.remoteStream : state.localStream"
          :name="
            ui.swapped
              ? callData?.patientName || 'Paciente'
              : callData?.professionalName
          "
          :role="ui.swapped ? 'Paciente' : 'Profissional'"
          :label="
            ui.swapped
              ? callData?.patientName || 'Paciente'
              : callData?.professionalName
          "
          :show-self="!ui.swapped"
          :audio-muted="ui.swapped ? state.remoteAudioMuted : false"
          @swap="swapVideos"
        />
      </main>

      <CallControls
        :muted="state.muted"
        :camera-off="state.cameraOff"
        @toggle-mute="toggleMute"
        @toggle-camera="toggleCamera"
        @end-call="showOutcomeModal"
      />
    </template>

    <div
      v-if="ui.showOutcome"
      class="outcome-overlay"
    >
      <div class="outcome-modal">
        <button
          class="outcome-close"
          title="Fechar sem salvar"
          @click="closeWindow"
        >
          <img
            src="@/assets/icons/delete.svg"
            alt="Fechar"
          >
        </button>
        <h2 class="outcome-title">
          Desfecho da Chamada
        </h2>
        <p class="outcome-subtitle">
          Chamada encerrada. Deseja adicionar um desfecho?
        </p>
        <textarea
          v-model="outcomeText"
          class="outcome-textarea"
          placeholder="Escreva o desfecho da chamada..."
          rows="5"
        />
        <div class="outcome-actions">
          <button
            class="button is-rounded outcome-skip"
            :disabled="ui.submittingOutcome"
            @click="skipOutcome"
          >
            Pular
          </button>
          <button
            class="button is-rounded default"
            :disabled="ui.submittingOutcome"
            @click="submitOutcome"
          >
            {{ ui.submittingOutcome ? "Enviando..." : "Salvar Desfecho" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref
} from "vue";
import { useRoute, useRouter } from "vue-router";
import CallTopBar from "@components/videoCall/CallTopBar.vue";
import VideoTile from "@components/videoCall/VideoTile.vue";
import CallControls from "@components/videoCall/CallControls.vue";
import { SignalRConnector } from "@/plugins/SignalRConnector.js";
import { agoraService } from "@/plugins/AgoraService";

const instance = getCurrentInstance();
const $axios = instance?.appContext.config.globalProperties.$axios;
const $Session = instance?.appContext.config.globalProperties.$Session;
const $userSessionName =
  instance?.appContext.config.globalProperties.$userSessionName;

const route = useRoute();
const router = useRouter();

// Estados da chamada: 'calling' | 'connected' | 'ended'
const callState = ref<"calling" | "connected" | "ended">("calling");

interface CallData {
  videoCallId: number;
  channelName: string;
  token: string;
  appId: string;
  uid: number;
  patientId: string;
  patientName: string;
  professionalName: string;
  professionalId: string;
}

const callData = ref<CallData | null>(null);
let signalRConnection: any = null;

const state = reactive({
  connected: false,
  muted: false,
  cameraOff: false,
  remoteAudioMuted: false,
  localStream: null as MediaStream | null,
  remoteStream: null as MediaStream | null
});

const ui = reactive({
  swapped: false,
  showOutcome: false,
  submittingOutcome: false,
  rejected: false
});

const outcomeText = ref("");
const callFinalized = ref(false);

const startedAt = ref<number>(0);
const now = ref<number>(Date.now());
let tick: number | undefined;

function loadCallData() {
  const pathParts = window.location.pathname.split("/");
  const callId = pathParts[pathParts.length - 1] || route.params.callId;
  const key = `videoCall_${callId}`;
  const stored = localStorage.getItem(key);

  if (stored) {
    callData.value = JSON.parse(stored);
    localStorage.removeItem(key);
  }
}

async function startSignalRListener() {
  if (!callData.value) return;

  try {
    signalRConnection = await SignalRConnector.startSignalR(
      "VideoCallHub",
      (connection: any) => {
        connection.on("CallAccepted", () => {
          onCallAccepted();
        });

        connection.on("CallRejected", () => {
          onCallRejected();
        });

        connection.on("CallEnded", () => {
          showOutcomeModal();
        });
      },
      0,
      callData.value.professionalId,
      "videocall"
    );

  } catch (error) {
    console.error("[SignalR] Erro ao conectar:", error);
  }
}

async function onCallAccepted() {
  console.log("[VideoCall] onCallAccepted - paciente aceitou a chamada");
  callState.value = "connected";
  startedAt.value = Date.now();
  tick = window.setInterval(() => (now.value = Date.now()), 250);

  if (!callData.value) return;

  agoraService.on("localStream", (stream) => {
    console.log("[Agora] localStream recebido");
    state.localStream = stream;
  });
  agoraService.on("remoteStream", (stream) => {
    console.log("[Agora] remoteStream recebido");
    state.remoteStream = stream;
  });
  agoraService.on("userLeft", () => {
    console.log("[Agora] userLeft - paciente saiu do canal");
    showOutcomeModal();
  });
  agoraService.on("remoteAudioMuted", (muted: boolean) => {
    console.log("[Agora] remoteAudioMuted:", muted);
    state.remoteAudioMuted = muted;
  });

  try {
    const { appId, channelName, token, uid } = callData.value;
    console.log("[Agora] joinChannel iniciando...", { channelName, uid });
    await agoraService.joinChannel(appId, channelName, token, uid);
    console.log("[Agora] joinChannel conectado com sucesso");
    state.connected = true;
  } catch (error) {
    console.error("[Agora] Erro ao conectar ao canal:", error);
  }
}

function onCallRejected() {
  console.log("[VideoCall] onCallRejected - chamada recusada pelo paciente");
  callFinalized.value = true;
  ui.rejected = true;
  setTimeout(() => {
    window.opener?.postMessage(
      { type: "call-finished", patientId: callData.value?.patientId },
      window.location.origin
    );
    closeWindow();
  }, 1500);
}

function buildAuthHeaders(): Record<string, string> {
  const token = $Session?.get($userSessionName)?.token?.Value;
  return token ? { Authorization: `bearer ${token}` } : {};
}

async function cancelCall() {
  console.log("[VideoCall] cancelCall - profissional cancelou a chamada");
  callFinalized.value = true;
  try {
    await $axios.post(
      `/api/call/${callData.value?.videoCallId}/cancell`,
      { statusName: "NaoAtendida" },
      { headers: buildAuthHeaders() }
    );
  } catch (error) {
    console.error("Erro ao cancelar chamada:", error);
  }
  window.opener?.postMessage(
    {
      type: "call-finished",
      patientId: callData.value?.patientId,
      status: "cancelada"
    },
    window.location.origin
  );
  closeWindow();
}
function toggleMute() {
  state.muted = !state.muted;
  agoraService.setMuted(state.muted);
}

function toggleCamera() {
  state.cameraOff = !state.cameraOff;
  agoraService.setCameraOff(state.cameraOff);
}

function swapVideos() {
  ui.swapped = !ui.swapped;
}


function closeWindow() {
  try {
    window.close();
  } catch (err) {
    console.warn("[Window] não conseguiu fechar:", err);
    router.push({ name: "patientTickets" }).catch(() => router.back());
  }
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

async function showOutcomeModal() {
  if (callState.value === "ended") return;
  console.log("[VideoCall] showOutcomeModal - encerrando chamada");
  const duration = startedAt.value > 0 ? formatDuration(Date.now() - startedAt.value) : null;
  callState.value = "ended";
  callFinalized.value = true;
  if (tick) clearInterval(tick);
  await agoraService.leave();
  console.log("[Agora] leave - desconectado do canal");
  state.localStream = null;
  state.remoteStream = null;

  try {
    await $axios.post(
      `/api/call/${callData.value?.videoCallId}/finish`,
      {},
      { headers: buildAuthHeaders() }
    );
  } catch (error) {
    console.error("Erro ao finalizar chamada:", error);
  }

  window.opener?.postMessage(
    { type: "call-finished", patientId: callData.value?.patientId },
    window.location.origin
  );

  ui.showOutcome = true;
}

function skipOutcome() {
  closeWindow();
}

async function submitOutcome() {
  ui.submittingOutcome = true;
  try {
    if (outcomeText.value.trim()) {
      await $axios.put(
        `/api/call/${callData.value?.videoCallId}`,
        { outcome: outcomeText.value.trim() },
        { headers: buildAuthHeaders() }
      );
    }
  } catch (error) {
    console.error("Erro ao enviar desfecho:", error);
  }

  closeWindow();
}

function buildCallActionUrl(callId: number, isRinging: boolean): string {
  const baseUrl = $axios?.defaults?.baseURL ?? "";
  const action = isRinging ? "cancell" : "finish";
  return `${baseUrl}/api/call/${callId}/${action}`;
}

function endCallOnWindowClose() {
  if (callFinalized.value || !callData.value) return;
  callFinalized.value = true;

  const isRinging = callState.value === "calling";

  fetch(buildCallActionUrl(callData.value.videoCallId, isRinging), {
    method: "POST",
    headers: { "Content-Type": "application/json", ...buildAuthHeaders() },
    body: JSON.stringify(isRinging ? { statusName: "NaoAtendida" } : {}),
    keepalive: true
  }).catch((error) =>
    console.error("Erro ao encerrar chamada no fechamento:", error)
  );

  window.opener?.postMessage(
    {
      type: "call-finished",
      patientId: callData.value.patientId,
      ...(isRinging ? { status: "cancelada" } : {})
    },
    window.location.origin
  );
}

function confirmBeforeClose(event: BeforeUnloadEvent) {
  if (callFinalized.value) return;
  event.preventDefault();
  event.returnValue = "";
}

onMounted(async () => {
  try {
    loadCallData();
  } catch (error) {
    console.error("Erro ao carregar callData:", error);
  }

  window.addEventListener("beforeunload", confirmBeforeClose);
  window.addEventListener("pagehide", endCallOnWindowClose);

  await startSignalRListener();
});

onBeforeUnmount(() => {
  if (tick) clearInterval(tick);
  agoraService.leave();

  window.removeEventListener("beforeunload", confirmBeforeClose);
  window.removeEventListener("pagehide", endCallOnWindowClose);

  if (signalRConnection) {
    SignalRConnector.stopSignalR(signalRConnection, "videocall");
  }

});
</script>

<style scoped>
@import "~@assets/css/views/callWindow.css";

.calling-overlay {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.calling-content {
  text-align: center;
  color: #fff;
}

.calling-animation {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid rgba(124, 58, 237, 0.5);
  animation: pulse 2s ease-out infinite;
}

.pulse-ring.delay-1 {
  animation-delay: 0.5s;
}

.pulse-ring.delay-2 {
  animation-delay: 1s;
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
  }
}

.phone-icon {
  color: #7c3aed;
  animation: shake 1.5s ease-in-out infinite;
}

@keyframes shake {
  0%,
  100% {
    transform: rotate(0deg);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: rotate(-10deg);
  }
  20%,
  40%,
  60%,
  80% {
    transform: rotate(10deg);
  }
}

.calling-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px;
}

.calling-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 40px;
}

.cancel-call-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #ef4444;
  color: #fff;
  border: none;
  border-radius: 50px;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-call-btn:hover {
  background-color: #dc2626;
}
.outcome-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.outcome-modal {
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  width: 90%;
  max-width: 500px;
  color: #484848;
  box-shadow: 0 8px 32px rgba(79, 63, 130, 0.2);
}

.outcome-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: #a8a8a8;
  cursor: pointer;
  padding: 0 4px;
}

.outcome-close:hover {
  color: #484848;
}

.outcome-title {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 8px;
  color: #4f3f82;
}

.outcome-subtitle {
  font-size: 14px;
  color: #a8a8a8;
  margin: 0 0 20px;
}

.outcome-textarea {
  width: 100%;
  background: #f6f6f6;
  border: 1px solid #dfdfdf;
  border-radius: 8px;
  color: #484848;
  padding: 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  box-sizing: border-box;
}

.outcome-textarea::placeholder {
  color: #a8a8a8;
}

.outcome-textarea:focus {
  outline: none;
  border-color: #9a397d;
}

.outcome-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
  justify-content: flex-end;
}

.outcome-actions .button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.outcome-skip {
  background: transparent;
  border: 1px solid #9a397d;
  color: #9a397d;
}
</style>
